import React, { useMemo, useEffect, useState } from "react";
import {
  Box,
  Checkbox,
  IconButton,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  TableCell,
  Typography,
  Icon,
  Autocomplete,
} from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDPagination from "components/MDPagination";

export type EntityRow = {
  id: string | number;
  isBroker: boolean;
  brokerId?: string | number;
  name?: string;
  salesRep?: string;
  email?: string;
  phone?: string;
  lastFunded?: string;
  birthday?: string;
  funded?: number;
  lastSubmission?: string;
  submissions?: number;
  declined?: number;
  defaults?: number;
  pendingResponse?: string | number | boolean;
  commissionsPaid?: number;
  [key: string]: any;
};

export type ColumnDef = {
  header: string;
  accessor: keyof EntityRow | string;
  width?: number | string;
  align?: "left" | "center" | "right";
  groupSummary?:
    | "sum"
    | "avg"
    | "count"
    | "min"
    | "max"
    | "first"
    | "last"
    | ((rows: EntityRow[], broker: EntityRow) => any);
  groupCell?: (args: { value: any; rows: EntityRow[]; broker: EntityRow }) => React.ReactNode;
  cell?: (args: { row: EntityRow; value: any }) => React.ReactNode;
};

type SortDir = "asc" | "desc";

export interface CollapsibleBrokersTableProps {
  rows: EntityRow[];
  columns: ColumnDef[];
  rowKey?: keyof EntityRow;
  defaultExpanded?: boolean;
  selectedKey?: EntityRow | null;
  onChangeSelected?: (key: EntityRow | null) => void;
  sortedColumn?: keyof EntityRow | null;
  sortDirection?: SortDir;
  onSort?: (accessor: keyof EntityRow) => void;
  onDoubleClick?: (partyId : any) => void;
  stripe?: boolean;
  stickyHeader?: boolean;
  enableScroll?: boolean;
  scrollHeight?: string;
  paginationEnabled?: boolean;
  showTotalEntries?: boolean;
  entriesPerPageOptions?: number[];
  defaultPageSize?: number;
  showBrokers?: boolean;
  showReps?: boolean;
}

const CHECKBOX_COL_W = 44;
const EXPAND_COL_W = 44;
const ROW_HEIGHT = 30;
const INDENT_PX = 24;
const COLOR_BROKER_BG = "#F8FAFC";
const COLOR_REP_BG = "#FFFFFF";
const COLOR_REP_BG_HOVER = "#E2E8F0";
const COLOR_REP_BG_STRIPE = "rgba(47,146,159,0.10)";
const COLOR_BORDER = "#E2E8F0";
const COLOR_BROKER_TEXT = "#1E293B";
const COLOR_HEADER_TEXT = "#64748B";
const COLOR_CELL_TEXT = "#334155";
const COLOR_CHECKBOX = "#3B82F6";
const COLOR_TOTALS_BG = "#F1F5F9";
const COLOR_TOTALS_TEXT = "#475569";
const COLOR_TOTALS_BORDER = "#E2E8F0";
const CELL_PX = 16;
const HEADER_HEIGHT = 36;

function getVal(obj: EntityRow, accessor: ColumnDef["accessor"]) {
  if (typeof accessor === "string") return obj[accessor];
  return undefined;
}

function aggregate(
  reps: EntityRow[],
  broker: EntityRow,
  accessor: ColumnDef["accessor"],
  mode: ColumnDef["groupSummary"]
) {
  if (!mode) return undefined;
  if (typeof mode === "function") return mode(reps, broker);
  if (mode === "count") return reps.length;
  const vals = reps
    .map((r) => getVal(r, accessor))
    .filter((v) => v !== undefined && v !== null) as any[];
  switch (mode) {
    case "sum":
      return vals.reduce((a, b) => a + (Number(b) || 0), 0);
    case "avg":
      return vals.length ? vals.reduce((a, b) => a + (Number(b) || 0), 0) / vals.length : 0;
    case "min":
      return vals.reduce((a: any, b: any) => (a < b ? a : b), vals[0]);
    case "max":
      return vals.reduce((a: any, b: any) => (a > b ? a : b), vals[0]);
    case "first":
      return reps[0] ? getVal(reps[0], accessor) : getVal(broker, accessor);
    case "last":
      return reps.length ? getVal(reps[reps.length - 1], accessor) : getVal(broker, accessor);
    default:
      return getVal(broker, accessor);
  }
}

const CollapsibleBrokersTable: React.FC<CollapsibleBrokersTableProps> = ({
  rows,
  columns,
  rowKey = "id",
  defaultExpanded = true,
  selectedKey,
  onChangeSelected,
  sortedColumn: externalSortedColumn,
  sortDirection: externalSortDirection,
  onSort,
  stripe = false,
  stickyHeader = true,
  enableScroll = true,
  scrollHeight = "65vh",
  paginationEnabled = true,
  showTotalEntries = true,
  entriesPerPageOptions = [20, 50, 75, 100],
  defaultPageSize = 10,
  showBrokers = true,
  showReps = true,
  onDoubleClick,
}) => {
  const [internalSortedColumn, setInternalSortedColumn] = useState<keyof EntityRow | null>(null);
  const [internalSortDirection, setInternalSortDirection] = useState<SortDir>("asc");
  const sortedColumn = externalSortedColumn ?? internalSortedColumn;
  const sortDirection: SortDir = externalSortDirection ?? internalSortDirection;

  const [selectedBroker, setSelectedBroker] = useState<EntityRow | null>(null);
  const selected = selectedKey ?? selectedBroker;

  const [internalSelectedRepKeys, setInternalSelectedRepKeys] = useState<any[]>([]);
  const [fullSelectBrokerId, setFullSelectBrokerId] = useState<string | null>(null);

  const setSelected = (broker: EntityRow | null) => {
    setSelectedBroker(broker ?? null);
    if (onChangeSelected) onChangeSelected(broker ?? null);
  };

  const handleSort = (accessor: keyof EntityRow) => {
    if (onSort) {
      onSort(accessor);
    } else {
      if (sortedColumn === accessor) {
        setInternalSortDirection((d) => (d === "asc" ? "desc" : "asc"));
      } else {
        setInternalSortedColumn(accessor);
        setInternalSortDirection("asc");
      }
    }
  };

  const getSortedState = (accessor: keyof EntityRow) => {
    if (sortedColumn === accessor) return sortDirection === "asc" ? "asce" : "desc";
    return "none";
  };

  const sortedBrokers = useMemo(() => {
    if (!sortedColumn) return rows.filter((r) => r.isBroker);
    const dir = sortDirection === "asc" ? 1 : -1;
    const brokers = rows.filter((r) => r.isBroker);
    return [...brokers].sort((a, b) => {
      const av = a[sortedColumn as keyof EntityRow] as any;
      const bv = b[sortedColumn as keyof EntityRow] as any;
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === "number" && typeof bv === "number") return (av - bv) * dir;
      return String(av).localeCompare(String(bv)) * dir;
    });
  }, [rows, sortedColumn, sortDirection]);

  const sortedReps = useMemo(() => {
    if (!sortedColumn) return rows.filter((r) => !r.isBroker);
    const dir = sortDirection === "asc" ? 1 : -1;
    const reps = rows.filter((r) => !r.isBroker);
    return [...reps].sort((a, b) => {
      const av = a[sortedColumn as keyof EntityRow] as any;
      const bv = b[sortedColumn as keyof EntityRow] as any;
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === "number" && typeof bv === "number") return (av - bv) * dir;
      return String(av).localeCompare(String(bv)) * dir;
    });
  }, [rows, sortedColumn, sortDirection]);

  const brokerNameById = useMemo(() => {
    const m = new Map<string, string>();
    rows.forEach((r) => {
      if (r.isBroker) {
        const id = String(r.id);
        const nm = r.name ?? r.brokerName ?? "";
        if (id && nm) m.set(id, nm);
      }
    });
    rows.forEach((r) => {
      if (!r.isBroker) {
        const bid = String(r.brokerId ?? "");
        if (!bid) return;
        if (!m.has(bid)) {
          const nm =
            r.brokerName ??
            r.broker ??
            r.brokerShop ??
            r.brokerLabel ??
            (typeof r.name === "string" ? r.name : "") ??
            "";
          if (nm) m.set(bid, nm);
        }
      }
    });
    return m;
  }, [rows]);

  const repsByBrokerId = useMemo(() => {
    const m = new Map<string, EntityRow[]>();
    sortedReps.forEach((r) => {
      const k = String(r.brokerId ?? "");
      if (!m.has(k)) m.set(k, []);
      m.get(k)!.push(r);
    });
    return m;
  }, [sortedReps]);

  const groupsAll = useMemo(() => {
    const byId = new Map<string, EntityRow>();
    sortedBrokers.forEach((b) => byId.set(String(b.id), b));
    const map = new Map<string, { broker: EntityRow; reps: EntityRow[] }>();
    sortedReps.forEach((r) => {
      const k = String(r.brokerId ?? "");
      if (!map.has(k)) {
        const displayName = byId.get(k)?.name ?? brokerNameById.get(k) ?? String(k);
        map.set(k, {
          broker: byId.get(k) || ({ id: k, isBroker: true, name: displayName } as EntityRow),
          reps: [],
        });
      }
      map.get(k)!.reps.push(r);
    });
    const ordered: Array<{ broker: EntityRow; reps: EntityRow[] }> = [];
    const used = new Set<string>();
    sortedBrokers.forEach((b) => {
      const k = String(b.id);
      if (map.has(k)) {
        ordered.push(map.get(k)!);
        used.add(k);
      } else {
        ordered.push({ broker: b, reps: [] });
        used.add(k);
      }
    });
    map.forEach((v, k) => {
      if (!used.has(k)) ordered.push(v);
    });
    return ordered;
  }, [sortedBrokers, sortedReps, brokerNameById]);

  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  useEffect(() => {
    const next: Record<string, boolean> = {};
    groupsAll.forEach(({ broker }) => {
      const k = String(broker.id);
      next[k] = expanded[k] ?? defaultExpanded;
    });
    setExpanded(next);
  }, [groupsAll, defaultExpanded]);

  const totals = useMemo(() => {
    const totalsSource: EntityRow[] =
      showReps && !showBrokers
        ? sortedReps
        : !showReps && !showBrokers
        ? []
        : rows.filter((row) => row.isBroker);
    return columns.reduce((acc, column) => {
      const accessor = String(column.accessor);
      const sumNames = new Set([
        "funded",
        "submissions",
        "declined",
        "defaults",
        "pendingResponse",
        "commissionsPaid",
        "commissionsOwed",
        "renewals",
      ]);
      const isDate = accessor === "lastFunded" || accessor === "lastSubmission";
      const toNumber = (v: unknown): number | null => {
        if (typeof v === "number" && Number.isFinite(v)) return v;
        if (typeof v === "string" && v.trim() !== "" && !Number.isNaN(Number(v))) return Number(v);
        return null;
      };
      const shouldAutoSumByName =
        /^total/i.test(accessor) || /Count$/i.test(accessor) || /^sum/i.test(accessor);
      const shouldSum = (() => {
        if (sumNames.has(accessor) || shouldAutoSumByName) return true;
        let numeric = 0,
          total = 0;
        for (const row of totalsSource) {
          const val = getVal(row, accessor);
          if (val !== null && val !== undefined && val !== "") {
            total++;
            if (toNumber(val) !== null) numeric++;
          }
        }
        return total > 0 && numeric / total >= 0.8;
      })();
      if (isDate) {
        const dates = totalsSource.map((r) => getVal(r, accessor)).filter(Boolean) as (
          | string
          | Date
        )[];
        acc[accessor] =
          dates.length === 0
            ? ""
            : dates.reduce(
                (latest, d) => (new Date(d as any) > new Date(latest as any) ? d : latest),
                dates[0]
              );
        return acc;
      }
      if (shouldSum) {
        acc[accessor] = totalsSource.reduce((s, r) => {
          const n = toNumber(getVal(r, accessor));
          return s + (n ?? 0);
        }, 0);
        return acc;
      }
      acc[accessor] = "";
      return acc;
    }, {} as Record<string, any>);
  }, [rows, columns, showBrokers, showReps, sortedReps]);

  const isBrokerRowChecked = (broker: EntityRow) =>
    selected && String(selected.id) === String(broker.id);

  const isRepRowChecked = (rep: EntityRow) => {
    const repBrokerId = String(rep.brokerId ?? "");
    if (selected && String(selected.id) === repBrokerId && fullSelectBrokerId === repBrokerId)
      return true;
    return internalSelectedRepKeys.includes(rep[rowKey]);
  };

  const toggleGroup = (brokerId: string, repKeys: any[]) => {
    const brokerObj = rows.find((r) => r.isBroker && String(r.id) === String(brokerId)) as
      | EntityRow
      | undefined;
    if (selected && String(selected.id) === brokerId) {
      setSelected(null);
      setInternalSelectedRepKeys([]);
      setFullSelectBrokerId(null);
    } else {
      const displayName = brokerObj?.name ?? brokerNameById.get(brokerId) ?? String(brokerId);
      const b =
        brokerObj ??
        ({
          id: brokerId,
          isBroker: true,
          name: displayName,
        } as EntityRow);
      setSelected(b);
      setInternalSelectedRepKeys(repKeys);
      setFullSelectBrokerId(brokerId);
    }
  };

  const toggleRep = (rep: EntityRow) => {
    const brokerId = String(rep.brokerId ?? "");
    const brokerObj =
      rows.find((r) => r.isBroker && String(r.id) === brokerId) ??
      ({
        id: brokerId,
        isBroker: true,
        name: brokerNameById.get(brokerId) ?? String(brokerId),
      } as EntityRow);
    const repKeysForBroker = rows
      .filter((r) => !r.isBroker && String(r.brokerId ?? "") === brokerId)
      .map((r) => r[rowKey]);
    setSelected(brokerObj);
    setFullSelectBrokerId(null);
    setInternalSelectedRepKeys((prev) => {
      const filtered = prev.filter((k) => repKeysForBroker.includes(k));
      const key = rep[rowKey];
      return filtered.includes(key) ? filtered.filter((k) => k !== key) : [...filtered, key];
    });
  };

  const colStyles = useMemo(() => {
    const autoCols = columns.filter((c) => c.width === undefined);
    const totalFixedWidth = CHECKBOX_COL_W + EXPAND_COL_W;
    const equalWidthExpr = `calc((100% - ${totalFixedWidth}px) / ${autoCols.length || 1})`;
    return columns.map((c) => {
      if (typeof c.width === "number") return `${c.width}px`;
      if (typeof c.width === "string") return c.width;
      return equalWidthExpr;
    });
  }, [columns]);

  const paginationMode: "groups" | "brokers" | "reps" = useMemo(() => {
    if (showBrokers && showReps) return "groups";
    if (showBrokers && !showReps) return "brokers";
    return "reps";
  }, [showBrokers, showReps]);

  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(defaultPageSize);

  const pageSizeOptions = useMemo(
    () => (paginationMode === "groups" ? entriesPerPageOptions : [25, 50, 75, 100]),
    [paginationMode, entriesPerPageOptions]
  );

  useEffect(() => {
    if (!pageSizeOptions.includes(pageSize)) {
      setPageSize(pageSizeOptions[0]);
      setPageIndex(0);
    }
  }, [pageSizeOptions, pageSize]);

  const totalItems = useMemo(() => {
    switch (paginationMode) {
      case "groups":
        return groupsAll.length;
      case "brokers":
        return sortedBrokers.length;
      case "reps":
        return sortedReps.length;
      default:
        return 0;
    }
  }, [paginationMode, groupsAll.length, sortedBrokers.length, sortedReps.length]);

  const pageCount = Math.max(1, Math.ceil(totalItems / pageSize));
  const canPreviousPage = pageIndex > 0;
  const canNextPage = pageIndex < pageCount - 1;
  const start = paginationEnabled ? pageIndex * pageSize : 0;
  const end = paginationEnabled ? Math.min(start + pageSize, totalItems) : totalItems;

  const groups = useMemo(() => {
    if (!paginationEnabled) return groupsAll;
    switch (paginationMode) {
      case "groups":
        return groupsAll.slice(start, end);
      case "brokers": {
        const slice = sortedBrokers.slice(start, end);
        return slice.map((b) => ({
          broker: b,
          reps: repsByBrokerId.get(String(b.id)) ?? [],
        }));
      }
      case "reps": {
        const repsPage = sortedReps.slice(start, end);
        return [
          {
            broker: { id: "__reps__", isBroker: true, name: "" } as EntityRow,
            reps: repsPage,
          },
        ];
      }
      default:
        return groupsAll;
    }
  }, [
    paginationEnabled,
    paginationMode,
    groupsAll,
    sortedBrokers,
    sortedReps,
    repsByBrokerId,
    start,
    end,
  ]);

  useEffect(() => {
    if (pageIndex > pageCount - 1) setPageIndex(Math.max(0, pageCount - 1));
  }, [pageCount, pageIndex]);

  const previousPage = () => canPreviousPage && setPageIndex((p) => p - 1);
  const nextPage = () => canNextPage && setPageIndex((p) => p + 1);

  const unitLabel =
    paginationMode === "groups"
      ? "broker groups"
      : paginationMode === "brokers"
      ? "brokers"
      : "sales reps";

  return (
    <MDBox sx={{ width: "100%" }}>
      <TableContainer
        sx={{
          boxShadow: "none",
          width: "100%",
          border: `1px solid ${COLOR_BORDER}`,
          borderRadius: "8px",
          marginTop: 0,
          maxHeight: scrollHeight,
          overflow: "auto",
          position: "relative",
          padding: 0,
          height: enableScroll ? scrollHeight : "auto",
          "& .MuiTableHead-root": {
            position: "sticky",
            top: -18,
            zIndex: 10,
          },
          "& .MuiTableRow-head": {
            position: "sticky",
            top: -18,
            zIndex: 11,
          },
          "& .MuiTableCell-head": {
            position: "sticky",
            top: 0,
            zIndex: 12,
            backgroundColor: "#FFFFFF !important",
          },
        }}
      >
        <Table  size="small"  stickyHeader sx={{ tableLayout: "fixed", borderSpacing: 0 , margin: 0,borderCollapse: "collapse",}}>
          <colgroup>
            <col style={{ width: `${CHECKBOX_COL_W + EXPAND_COL_W}px` }} />
            {columns.map((c, i) => (
              <col
                key={i}
                style={{
                  width: colStyles[i],
                  minWidth: typeof c.width === "number" ? `${c.width}px` : "auto",
                }}
              />
            ))}
          </colgroup>

          <TableHead sx={{bgcolor: "#FFFFFF"}}>
            <TableRow sx={{ height: HEADER_HEIGHT}}>
              <TableCell
                padding="checkbox"
                align="center"
                sx={{
                  width: `${CHECKBOX_COL_W + EXPAND_COL_W}px`,
                  minWidth: `${CHECKBOX_COL_W + EXPAND_COL_W}px`,
                  maxWidth: `${CHECKBOX_COL_W + EXPAND_COL_W}px`,
                  px: `${CELL_PX}px`,
                  boxSizing: "border-box",
                  borderBottom: `1px solid ${COLOR_BORDER}`,
                  left: 0,
                  zIndex: 12,
                  bgcolor: "#FFFFFF",
                  top: 0,
                  padding: "4px 8px",
                  borderTop: "none",
                  position: "sticky !important",
                }}
              >
                <Box sx={{ width: CHECKBOX_COL_W, height: "20px" }} />
              </TableCell>

              {columns.map((c, index) => {
                const sorted = getSortedState(c.accessor as keyof EntityRow);
                return (
                  <TableCell
                    key={String(c.accessor)}
                    align={c.align ?? "left"}
                    sx={{
                      width: colStyles[index],
                      minWidth: typeof c.width === "number" ? `${c.width}px` : "auto",
                      px: `${CELL_PX}px`,
                      whiteSpace: "nowrap",
                      color: COLOR_HEADER_TEXT,
                      borderBottom: `1px solid ${COLOR_BORDER}`,
                      cursor: "pointer",
                      boxSizing: "border-box",
                      top: 0,
                      zIndex: 11,
                      bgcolor: "#FFFFFF",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      fontSize: "10px",
                      fontWeight: 700,
                      userSelect: "none",
                      padding: "4px 8px",
                      borderTop: "none",
                      position: "sticky !important",
                      ...(index === 0 && {
                        left: `${CHECKBOX_COL_W + EXPAND_COL_W}px`, 
                      }),
                    }}
                    onClick={() => handleSort(c.accessor as keyof EntityRow)}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent:
                          c.align === "right"
                            ? "flex-end"
                            : c.align === "center"
                            ? "center"
                            : "flex-start",
                        gap: 0.5,
                        width: "100%",
                        height: "100%",
                      }}
                    >
                      <Typography
                        noWrap
                        sx={{
                          fontFamily: "Roboto",
                          fontWeight: 700,
                          fontSize: "10px",
                          lineHeight: "1.2",
                          color: COLOR_HEADER_TEXT,
                          flex: 1,
                          textAlign: c.align ?? "left",
                        }}
                      >
                        {c.header}
                      </Typography>
                      <Box
                        sx={{ display: "flex", flexDirection: "column", ml: 0.25, flexShrink: 0 }}
                      >
                        <Icon
                          sx={{
                            fontSize: "16px !important",
                            color: sorted === "asce" ? "text.primary" : "text.secondary",
                            opacity: sorted === "asce" ? 1 : 0.5,
                            lineHeight: 0.8,
                          }}
                        >
                          arrow_drop_up
                        </Icon>
                        <Icon
                          sx={{
                            fontSize: "16px !important",
                            color: sorted === "desc" ? "text.primary" : "text.secondary",
                            opacity: sorted === "desc" ? 1 : 0.5,
                            lineHeight: 0.8,
                            marginTop: "-8px",
                          }}
                        >
                          arrow_drop_down
                        </Icon>
                      </Box>
                    </Box>
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>

          <TableBody>
            {groups.map(({ broker, reps }) => {
              const k = String(broker.id);
              const isOpen = (expanded[k] ?? defaultExpanded) || !showBrokers;
              const repKeys = reps.map((r) => r[rowKey]);
              const groupVals: Record<string, any> = {};
              columns.forEach((c, idx) => {
                const val =
                  c.groupSummary !== undefined
                    ? aggregate(reps, broker, c.accessor, c.groupSummary)
                    : getVal(broker, c.accessor);
                groupVals[String(c.accessor)] = idx === 0 ? broker.name ?? val : val;
              });
              return (
                <React.Fragment key={k}>
                  {showBrokers && (
                    <TableRow
                     onDoubleClick={() => onDoubleClick?.(broker.id)}
                      sx={{
                        bgcolor: COLOR_BROKER_BG,
                        borderBottom: `1px solid ${COLOR_BORDER}`,
                        "&:hover": { 
                          bgcolor: COLOR_REP_BG_HOVER,
                          cursor: "pointer",
                          paddingY: "2px !important"
                        },
                      }}
                    >
                      <TableCell
                        padding="checkbox"
                        align="center"
                        sx={{
                          width: `${CHECKBOX_COL_W + EXPAND_COL_W}px`,
                          minWidth: `${CHECKBOX_COL_W + EXPAND_COL_W}px`,
                          maxWidth: `${CHECKBOX_COL_W + EXPAND_COL_W}px`,
                          px: `${CELL_PX}px`,
                          boxSizing: "border-box",
                          left: 0,
                          zIndex: 2,
                          bgcolor: "inherit",
                          borderBottom: `1px solid ${COLOR_BORDER}`,
                          verticalAlign: "middle",
                          paddingY: "2px !important"
                        }}
                      >
                        <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                          <Checkbox
                            size="small"
                            sx={{ color: COLOR_CHECKBOX }}
                            checked={isBrokerRowChecked(broker)}
                            indeterminate={false}
                            onChange={() => toggleGroup(k, repKeys as any[])}
                          />
                          <IconButton
                            size="small"
                            onClick={() => setExpanded((p) => ({ ...p, [k]: !isOpen }))}
                            sx={{ color: COLOR_BROKER_TEXT }}
                            disabled={!showReps}
                          >
                            {isOpen ? <ExpandLess /> : <ExpandMore />}
                          </IconButton>
                        </Box>
                      </TableCell>

                      {columns.map((c, idx) => {
                        const val = groupVals[String(c.accessor)];
                        return (
                          <TableCell
                            key={String(c.accessor)}
                            align={c.align ?? "left"}
                            sx={{
                              width: colStyles[idx],
                              minWidth: typeof c.width === "number" ? `${c.width}px` : "auto",
                              color: COLOR_BROKER_TEXT,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              boxSizing: "border-box",
                              px: `${CELL_PX}px`,
                              borderBottom: `1px solid ${COLOR_BORDER}`,
                              verticalAlign: "middle",
                              paddingY: "2px !important"

                            }}
                          >
                            {idx === 0 ? (
                              <Typography
                                noWrap
                                sx={{
                                  fontFamily: "Roboto",
                                  fontWeight: 700,
                                  fontSize: "12px",
                                  lineHeight: "140%",
                                }}
                              >
                                {val ?? ""}
                              </Typography>
                            ) : c.groupCell ? (
                              c.groupCell({ value: val, rows: reps, broker })
                            ) : (
                              <Typography
                                noWrap
                                sx={{
                                  fontFamily: "Roboto",
                                  fontWeight: 700,
                                  fontSize: "12px",
                                  lineHeight: "140%",
                                }}
                              >
                                {val ?? ""}
                              </Typography>
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  )}

                  {showReps &&
                    reps.map((r, ri) => {
                      const isStriped = stripe && ri % 2 === 1;
                      const bgColor = isStriped ? COLOR_REP_BG_STRIPE : COLOR_REP_BG;
                      return (
                        <TableRow
                          key={String(r[rowKey])}
                          hover
                          onDoubleClick={() => onDoubleClick?.(r.brokerId)}
                          sx={{
                            display: isOpen ? "table-row" : "none",
                            bgcolor: bgColor,
                            "&:hover": { bgcolor: COLOR_REP_BG_HOVER },
                            borderBottom: `1px solid ${COLOR_BORDER}`,
                            paddingY: "2px !important"
                          }}
                        >
                          <TableCell
                            padding="checkbox"
                            align="center"
                            sx={{
                              width: `${CHECKBOX_COL_W + EXPAND_COL_W}px`,
                              minWidth: `${CHECKBOX_COL_W + EXPAND_COL_W}px`,
                              maxWidth: `${CHECKBOX_COL_W + EXPAND_COL_W}px`,
                              px: `${CELL_PX}px`,
                              boxSizing: "border-box",
                              left: 0,
                              zIndex: 1,
                              borderBottom: `1px solid ${COLOR_BORDER}`,
                              verticalAlign: "middle",
                              paddingY: "2px !important"
                            }}
                          >
                            <Box
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                              gap={0.5}
                            >
                              <Checkbox
                                size="small"
                                checked={isRepRowChecked(r)}
                                onChange={() => toggleRep(r)}
                              />
                              <Box sx={{ width: EXPAND_COL_W - 12, bgcolor: "transparent" }} />
                            </Box>
                          </TableCell>

                          {columns.map((c, idx) => {
                            const val = getVal(r, c.accessor);
                            const content = c.cell
                              ? c.cell({ row: r, value: val })
                              : val == null
                              ? ""
                              : String(val);
                            return (
                              <TableCell
                                key={String(c.accessor)}
                                align={c.align ?? "left"}
                                sx={{
                                  width: colStyles[idx],
                                  minWidth: typeof c.width === "number" ? `${c.width}px` : "auto",
                                  color: COLOR_CELL_TEXT,
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  boxSizing: "border-box",
                                  px: `${CELL_PX}px`,
                                  borderBottom: `1px solid ${COLOR_BORDER}`,
                                  verticalAlign: "middle",
                                  paddingY: "2px !important"

                                }}
                              >
                                {idx === 0 ? (
                                  <Box sx={{ ml: `${INDENT_PX}px` }}>
                                    <Typography
                                      noWrap
                                      sx={{
                                        fontFamily: "Roboto",
                                        fontWeight: 400,
                                        fontSize: "12px",
                                        lineHeight: "140%",
                                      }}
                                    >
                                      {content}
                                    </Typography>
                                  </Box>
                                ) : (
                                  <Typography
                                    noWrap
                                    sx={{
                                      fontFamily: "Roboto",
                                      fontWeight: 400,
                                      fontSize: "12px",
                                      lineHeight: "140%",
                                    }}
                                  >
                                    {content}
                                  </Typography>
                                )}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      );
                    })}
                </React.Fragment>
              );
            })}
          </TableBody>

          <TableFooter>
            <TableRow>
              <TableCell
                colSpan={1}
                align="center"
                sx={{
                  position: "sticky",
                  bottom: 0,
                  zIndex: 10,
                  bgcolor: COLOR_TOTALS_BG,
                  color: COLOR_TOTALS_TEXT,
                  borderBottom: `1px solid ${COLOR_TOTALS_BORDER}`,
                  borderTop: `2px solid ${COLOR_TOTALS_BORDER}`,
                  height: ROW_HEIGHT,
                  px: `${CELL_PX}px`,
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "Roboto",
                    fontWeight: 700,
                    fontSize: "12px",
                    lineHeight: "140%",
                    color: COLOR_TOTALS_TEXT,
                  }}
                >
                  TOTALS
                </Typography>
              </TableCell>

              {columns.map((c, idx) => {
                const totalValue = totals[String(c.accessor)];
                let displayValue = totalValue;
                if (typeof totalValue === "number") {
                  if (c.accessor === "commissionsPaid" || c.accessor === "commissionsOwed") {
                    displayValue = `$${totalValue.toLocaleString()}`;
                  } else {
                    displayValue = totalValue.toLocaleString();
                  }
                }
                return (
                  <TableCell
                    key={`total-${String(c.accessor)}`}
                    align={c.align ?? "left"}
                    sx={{
                      width: colStyles[idx],
                      position: "sticky",
                      bottom: 0,
                      zIndex: 10,
                      bgcolor: COLOR_TOTALS_BG,
                      color: COLOR_TOTALS_TEXT,
                      borderBottom: `1px solid ${COLOR_TOTALS_BORDER}`,
                      borderTop: `2px solid ${COLOR_TOTALS_BORDER}`,
                      height: ROW_HEIGHT,
                      px: `${CELL_PX}px`,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: "Roboto",
                        fontWeight: 700,
                        fontSize: "12px",
                        lineHeight: "140%",
                        color: COLOR_TOTALS_TEXT,
                      }}
                    >
                      {displayValue || ""}
                    </Typography>
                  </TableCell>
                );
              })}
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>

      {paginationEnabled && (
        <MDBox
          display="flex"
          flexDirection={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          p={!showTotalEntries && (pageCount === 1 ? 0 : 3)}
          gap={2}
          mt={2}
        >
          {showTotalEntries && (
            <div className="d-flex">
              <MDBox mb={{ xs: 3, sm: 0 }}>
                <MDTypography variant="button" color="secondary" fontWeight="regular">
                  Showing {totalItems === 0 ? 0 : start + 1} to {end} of {totalItems} {unitLabel}
                </MDTypography>
              </MDBox>
              <MDBox display="flex" alignItems="center">
                <Autocomplete
                  disableClearable
                  value={String(pageSize)}
                  options={pageSizeOptions.map(String)}
                  onChange={(_, v) => {
                    const next = parseInt(v || String(pageSizeOptions[0]), 10);
                    setPageSize(next);
                    setPageIndex(0);
                  }}
                  size="small"
                  sx={{ width: "5rem", ml: "10px" }}
                  renderInput={(params) => <MDInput {...params} />}
                />
                <MDTypography variant="caption" color="secondary">
                  &nbsp;&nbsp;entries per page
                </MDTypography>
              </MDBox>
            </div>
          )}

          {pageCount > 1 && (
            <MDPagination variant="gradient" color="primary">
              {canPreviousPage && (
                <MDPagination item onClick={previousPage}>
                  <Icon sx={{ fontWeight: "bold" }}>chevron_left</Icon>
                </MDPagination>
              )}
              {Array.from({ length: pageCount }, (_, p) => (
                <MDPagination key={p} item active={p === pageIndex} onClick={() => setPageIndex(p)}>
                  {p + 1}
                </MDPagination>
              ))}
              {canNextPage && (
                <MDPagination item onClick={nextPage}>
                  <Icon sx={{ fontWeight: "bold" }}>chevron_right</Icon>
                </MDPagination>
              )}
            </MDPagination>
          )}
        </MDBox>
      )}
    </MDBox>
  );
};

export default CollapsibleBrokersTable;